import {imageMime, heicImage} from './imageFormats';

export const MAX_BROWSER_PREVIEW_BYTES = 32 * 1024 ** 2;
export const MAX_BROWSER_PREVIEW_PIXELS = 24 * 1000 ** 2;
const aborted = () => new DOMException('Просмотр отменён', 'AbortError');
const check = signal => {if (signal?.aborted) throw aborted();};
const workerProgram = `
self.onmessage=async function(event){
 try {
  const lib=await self.planiumHeicFactory();
  const images=new lib.HeifDecoder().decode(new Uint8Array(event.data.bytes));
  if(!images||!images.length||images.length>32)throw Error('invalid');
  const image=images.find(i=>i.is_primary())||images[0];
  const width=image.get_width(),height=image.get_height();
  if(!Number.isSafeInteger(width)||!Number.isSafeInteger(height)||width<1||height<1||width>32768||height>32768||width*height>event.data.maxPixels)throw Error('dimensions');
  const rgba=new Uint8ClampedArray(width*height*4);
  await new Promise((resolve,reject)=>image.display({data:rgba,width,height},v=>v?resolve():reject(Error('decode'))));
  self.postMessage({width,height,buffer:rgba.buffer},[rgba.buffer]);
 } catch(failure){self.postMessage({error:failure.message==='dimensions'?'dimensions':'decode'});}
};`;

export async function readPreviewBytes(url, signal) {
  check(signal);
  const parsed = new URL(url, window.location.href);
  if (!['https:', 'http:'].includes(parsed.protocol) || (parsed.protocol === 'http:' && !['127.0.0.1', 'localhost'].includes(parsed.hostname)) || parsed.username || parsed.password) {
    throw new Error('Небезопасный адрес изображения.');
  }
  let response;
  try {response = await fetch(parsed.href, {signal, credentials:'omit', redirect:'error', referrerPolicy:'no-referrer', cache:'no-store'});}
  catch (failure) {check(signal); throw new Error('Не удалось получить оригинал для просмотра. Проверьте соединение и повторите открытие файла.');}
  if (!response.ok) throw new Error('Не удалось получить оригинал для просмотра. Откройте файл повторно.');
  const declared = Number(response.headers.get('Content-Length'));
  if (declared > MAX_BROWSER_PREVIEW_BYTES) throw new Error('Этот оригинал слишком большой для преобразования в браузере. Скачайте файл.');
  if (!response.body?.getReader) {
    const bytes = await response.arrayBuffer(); check(signal);
    if (bytes.byteLength > MAX_BROWSER_PREVIEW_BYTES) throw new Error('Оригинал превышает лимит браузерного просмотра.');
    return bytes;
  }
  const reader=response.body.getReader(), chunks=[];let size=0;
  try {
    while(true){check(signal);const value=await reader.read();if(value.done)break;size+=value.value.byteLength;
      if(size>MAX_BROWSER_PREVIEW_BYTES)throw new Error('Оригинал превышает лимит браузерного просмотра.');chunks.push(value.value);}
  } finally {await reader.cancel().catch(()=>{});}
  check(signal);const joined=new Uint8Array(size);let at=0;
  for(const chunk of chunks){joined.set(chunk,at);at+=chunk.byteLength;}return joined.buffer;
}

export async function decodeHeicPixels(bytes, signal) {
  check(signal);
  if (bytes.byteLength > MAX_BROWSER_PREVIEW_BYTES) throw new Error('Оригинал слишком большой для браузерного просмотра.');
  if (!window.Worker || !window.WebAssembly) throw new Error('Браузер не поддерживает локальный просмотр HEIC. Скачайте оригинал.');
  const {codecSource} = await import('./vendor/heicCodecSource'); check(signal);
  const script=URL.createObjectURL(new Blob([codecSource, '\n', workerProgram], {type:'text/javascript'}));
  let worker;
  try {worker=new Worker(script);} catch {URL.revokeObjectURL(script);throw new Error('Браузер запретил локальный декодер HEIC. Скачайте оригинал.');}
  return new Promise((resolve,reject)=>{
    const stop=()=>{clearTimeout(timer);signal?.removeEventListener('abort',cancel);worker.terminate();URL.revokeObjectURL(script);};
    const cancel=()=>{stop();reject(aborted());};
    const timer=setTimeout(()=>{stop();reject(new Error('Браузер не успел преобразовать HEIC. Скачайте оригинал.'));},30000);
    signal?.addEventListener('abort',cancel,{once:true});
    worker.onerror=()=>{stop();reject(new Error('Не удалось прочитать HEIC в браузере. Оригинал можно скачать.'));};
    worker.onmessage=event=>{const data=event.data;stop();
      if(data.error)reject(new Error(data.error==='dimensions'?'Изображение больше 24 мегапикселей. Скачайте оригинал для просмотра.':'HEIC не удалось прочитать. Скачайте оригинал.'));
      else if(data.width*data.height>MAX_BROWSER_PREVIEW_PIXELS || data.buffer?.byteLength!==data.width*data.height*4)reject(new Error('Некорректный результат декодирования.'));
      else resolve(data);};
    worker.postMessage({bytes,maxPixels:MAX_BROWSER_PREVIEW_PIXELS},[bytes]);
  });
}

export async function browserPreview(node, url, signal) {
  const mime=imageMime(node);if(!mime)throw new Error('Этот файл не является поддерживаемым изображением.');
  if(Number(node.size)>MAX_BROWSER_PREVIEW_BYTES)throw new Error('Оригинал слишком большой для браузерного преобразования. Скачайте файл.');
  const bytes=await readPreviewBytes(url,signal);check(signal);
  let blob;
  if (heicImage(node)) {
    const pixels=await decodeHeicPixels(bytes,signal);check(signal);
    const canvas=document.createElement('canvas');canvas.width=pixels.width;canvas.height=pixels.height;
    const context=canvas.getContext('2d');if(!context)throw new Error('Браузер не смог подготовить изображение.');
    const image=context.createImageData(pixels.width,pixels.height);image.data.set(new Uint8ClampedArray(pixels.buffer));context.putImageData(image,0,0);
    blob=await new Promise(resolve=>canvas.toBlob(resolve,'image/jpeg',0.9));canvas.width=1;canvas.height=1;
    if(!blob)throw new Error('Не удалось подготовить изображение для просмотра.');
  } else {
    // Correct a generic server Content-Type without ever interpreting HTML or SVG.
    // Native raster decoding still validates the bytes of this newly typed Blob.
    blob=new Blob([bytes],{type:mime});
  }
  check(signal);const result=URL.createObjectURL(blob);return {url:result,dispose:()=>URL.revokeObjectURL(result)};
}
